import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { alpha, Box, Card, CardContent, Chip, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type {
  DeliveryItem,
  RecallCheckMatchRecall,
} from '../../../services/goods-receiving/goods-receiving.types';
import { formatDate } from '../../../utils/date-formatter';
import { getUnitLabel } from '../../../utils/units';
import { ItemRecallWarning } from './item-recall-warning';
import { LineAmountWarning } from './line-amount-warning';

interface DeliveryItemCardProps {
  item: DeliveryItem;
  isMobile: boolean;
  onRemove: () => void;
  onEdit: () => void;
  recallMatches?: RecallCheckMatchRecall[];
}

export function getMissingFields(item: DeliveryItem): string[] {
  const missing: string[] = [];
  if (!item.productName?.trim()) {
    missing.push('productName');
  }
  if (!item.batchNumber?.trim()) {
    missing.push('batchNumber');
  }
  // hasNoExpiry=true is an intentional shelf-stable declaration — the
  // XOR invariant (DB CHECK + service) guarantees expiryDate is null in
  // that case, so the item is complete without a date.
  if (!item.expiryDate?.trim() && !item.hasNoExpiry) {
    missing.push('expiryDate');
  }
  return missing;
}

/**
 * Wizard "next" gate for the items list. Expiry is a soft requirement: the
 * user may proceed after confirming they accept to ignore it, so it never
 * blocks. Name/batch stay hard requirements.
 */
export function getItemsGate(items: DeliveryItem[]): {
  hasBlockingMissing: boolean;
  hasMissingExpiry: boolean;
  canGoNext: boolean;
} {
  const hasBlockingMissing = items.some((item) =>
    getMissingFields(item).some((field) => field !== 'expiryDate'),
  );
  const hasMissingExpiry = items.some((item) => getMissingFields(item).includes('expiryDate'));
  return {
    hasBlockingMissing,
    hasMissingExpiry,
    canGoNext: items.length > 0 && !hasBlockingMissing,
  };
}

export function DeliveryItemCard({
  item,
  isMobile,
  onRemove,
  onEdit,
  recallMatches,
}: DeliveryItemCardProps) {
  const { t } = useTranslation('goodsReceiving');
  const missingFields = getMissingFields(item);
  const isIncomplete = missingFields.length > 0;

  return (
    <Card
      sx={{
        borderColor: isIncomplete ? 'warning.main' : undefined,
        bgcolor: isIncomplete ? (theme) => alpha(theme.palette.warning.main, 0.04) : undefined,
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            justifyContent: 'space-between',
            alignItems: 'start',
            gap: isMobile ? 1.5 : 0,
          }}
        >
          <ItemDetails item={item} isMobile={isMobile} recallMatches={recallMatches} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {item.labelPhotos.length > 0 && <PhotoIndicator count={item.labelPhotos.length} />}
            <IconButton
              onClick={onEdit}
              color="primary"
              size="small"
              aria-label={t('itemEditAria')}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              onClick={onRemove}
              color="error"
              size="small"
              aria-label={t('itemRemoveAria')}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        </Box>
        <LineAmountWarning
          unitPrice={item.unitPrice}
          quantity={item.quantity}
          lineTotal={item.lineTotal}
        />
        {isIncomplete && <MissingFieldsWarning missingFields={missingFields} onEdit={onEdit} />}
      </CardContent>
    </Card>
  );
}

interface ItemDetailsProps {
  item: DeliveryItem;
  isMobile: boolean;
  recallMatches?: RecallCheckMatchRecall[];
}

function ItemDetails({ item, isMobile, recallMatches }: ItemDetailsProps) {
  const { t } = useTranslation('goodsReceiving');
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 'medium',
          fontSize: isMobile ? '1rem' : undefined,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          wordBreak: 'break-word',

          ...(isMobile
            ? {
                maxHeight: '2.5em',
                lineHeight: '1.25em',
              }
            : {
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }),
        }}
      >
        {item.productName || (
          <Typography
            component="span"
            sx={{
              color: 'text.disabled',
              fontStyle: 'italic',
            }}
          >
            {t('itemMissingProductName')}
          </Typography>
        )}
      </Typography>
      <ItemRecallWarning recalls={recallMatches ?? []} />
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontSize: isMobile ? '0.875rem' : undefined,
          mt: 0.5,
        }}
      >
        {item.quantity} {item.unit ? getUnitLabel(item.unit) : ''}
        {item.unitPrice != null && item.currency && (
          <Typography component="span" sx={{ ml: 1, fontWeight: 'medium', color: 'text.primary' }}>
            {item.unitPrice.toFixed(2).replace('.', ',')} {item.currency}/{getUnitLabel(item.unit)}
          </Typography>
        )}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontSize: isMobile ? '0.875rem' : undefined,
          mt: 0.5,
        }}
      >
        {item.batchNumber ? (
          `${t('itemBatchPrefix')} ${item.batchNumber}`
        ) : (
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: 'text.disabled',
              fontStyle: 'italic',
            }}
          >
            {t('itemBatchPrefix')} —
          </Typography>
        )}
        {' • '}
        {item.expiryDate ? (
          `${t('itemExpiryPrefix')} ${formatDate(item.expiryDate)}`
        ) : item.hasNoExpiry ? null : (
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: 'text.disabled',
              fontStyle: 'italic',
            }}
          >
            {t('itemExpiryPrefix')} —
          </Typography>
        )}
      </Typography>
      {item.hasNoExpiry && (
        <Chip label={t('itemNoExpiryChip')} size="small" variant="outlined" sx={{ mt: 0.75 }} />
      )}
    </Box>
  );
}

interface PhotoIndicatorProps {
  count: number;
}

function PhotoIndicator({ count }: PhotoIndicatorProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        color: 'text.secondary',
      }}
    >
      <PhotoCameraIcon fontSize="small" />
      <Typography variant="caption">{count}</Typography>
    </Box>
  );
}

const MISSING_FIELD_KEYS: Record<string, string> = {
  productName: 'itemMissingProductName',
  batchNumber: 'itemMissingBatchNumber',
  expiryDate: 'itemMissingExpiryDate',
};

interface MissingFieldsWarningProps {
  missingFields: string[];
  onEdit: () => void;
}

function MissingFieldsWarning({ missingFields, onEdit }: MissingFieldsWarningProps) {
  const { t } = useTranslation('goodsReceiving');

  return (
    <Box
      onClick={onEdit}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        mt: 1.5,
        pt: 1.5,
        borderTop: 1,
        borderColor: 'divider',
        cursor: 'pointer',
        flexWrap: 'wrap',
      }}
    >
      <WarningAmberIcon fontSize="small" color="warning" />
      {missingFields.map((field) => (
        <Chip
          key={field}
          label={t(MISSING_FIELD_KEYS[field])}
          size="small"
          color="warning"
          variant="outlined"
        />
      ))}
    </Box>
  );
}
